// Общие money-хелперы для vitrine-экранов. Вынесено из копипаста (DUPLICATION_AUDIT §C1):
// currencySymbol-карта была символ-в-символ одинакова в 3 файлах, ru-RU-формат — в 4.

import { i18n } from '@/i18n';

const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', RUB: '₽', RUR: '₽', GBP: '£' };

/** Символ валюты по ISO-коду; неизвестный код — как есть. */
export function currencySymbol(code: string): string {
  return CURRENCY_SYMBOLS[(code ?? '').toUpperCase()] ?? code;
}

// ---------------------------------------------------------------- one way to write a sum (owner rule 25.10.2026)
//
// Every amount the cabinet shows goes through `formatDisplayAmount` (or a wrapper of it below) and is written in the
// CABINET language — the app's i18n locale, the single source of truth: ru «4 950,50», en «4,950.50», zh «4,950.50».
// No screen pins its own locale or swaps separators; an input field may keep its typing format, the totals around it
// may not.

// ---------------------------------------------------------------- display precision (owner rule 25.10.2026)
//
// What a person reads: fiat with exactly 2 decimals, crypto with 2 to 4 (trailing zeros trimmed past the second).
// A rate takes the precision of the currency it is denominated in (RUB in «80,33 RUB per USD», USDT in «USDT per
// RUB»); a fiat rate below 1 keeps up to 4 so «0,0104» never collapses to «0,01». Only the text is rounded,
// HALF_UP; inputs, stored and computed values keep their full precision.

/** Crypto assets the cabinet shows; any other code is treated as fiat. */
export const CRYPTO_CODES: ReadonlySet<string> = new Set(['USDT', 'USDC', 'BTC', 'ETH', 'TRX', 'TON', 'SOL', 'DAI']);

export function isCrypto(code: string | null | undefined): boolean {
  return CRYPTO_CODES.has((code ?? '').trim().toUpperCase());
}

/** Fraction digits a person sees for an amount in `code`: fiat 2..2, crypto 2..4. */
export function displayDigits(code: string | null | undefined): { min: number; max: number } {
  return isCrypto(code) ? { min: 2, max: 4 } : { min: 2, max: 2 };
}

/** The decimal string of `v` (a comma separator accepted), or null when it is not a finite number. */
function decimalString(v: string | number): string | null {
  const s = typeof v === 'number' ? String(v) : String(v).trim().replace(/\s/g, '').replace(',', '.');
  return s !== '' && Number.isFinite(Number(s)) ? s : null;
}

/**
 * Formats a decimal string with Intl's HALF_UP («halfExpand»). The string goes to Intl as the exact decimal it is
 * (no binary float detour: «1.005» → «1,01»); a number arrives here as its shortest decimal form.
 */
function formatDecimal(s: string, min: number, max: number, locale: string): string {
  // Intl accepts a decimal string at runtime (ES2023); the ES2022 lib types only declare number.
  return new Intl.NumberFormat(locale, { minimumFractionDigits: min, maximumFractionDigits: max })
    .format(s as unknown as number);
}

/** An amount as a person reads it, without the currency: «5 250,00» (fiat), «12,3457» / «5 250,00» (crypto). */
export function formatDisplayAmount(v: string | number | null | undefined, code: string | null | undefined,
                                    locale: string = i18n.global.locale.value): string {
  if (v === null || v === undefined) return '—';
  const s = decimalString(v);
  if (s == null) return String(v);
  const { min, max } = displayDigits(code);
  return formatDecimal(s, min, max, locale);
}

/**
 * `s` rounded UP (toward +∞) to `digits` decimals, as an exact decimal string — no binary float on the way:
 * «2129.421825», 4 → «2129.4219». A value with no more decimals than that comes back as it is.
 */
function ceilDecimal(s: string, digits: number): string {
  const neg = s.startsWith('-');
  const body = s.replace(/^[-+]/, '');
  if (!/^\d*(\.\d*)?$/.test(body)) return s;               // an exponent form: left to the formatter as it is
  const [int = '0', frac = ''] = body.split('.');
  if (frac.length <= digits || /^0*$/.test(frac.slice(digits))) return s;
  const kept = frac.slice(0, digits);
  // toward +∞: a negative value only loses its extra digits
  if (neg) return `-${int}${digits ? `.${kept}` : ''}`;
  const units = (BigInt((int || '0') + kept) + BigInt(1)).toString().padStart(digits + 1, '0');
  return digits ? `${units.slice(0, -digits)}.${units.slice(-digits)}` : units;
}

/**
 * An amount the client must PAY (review M5): rounded UP to what is shown, so paying the shown amount always covers it
 * — HALF_UP showed «2 129,4218» for 2129.421825 USDT and a client who copied it underpaid. Received and informational
 * amounts stay with {@link formatDisplayAmount}.
 */
export function formatAmountToPay(v: string | number | null | undefined, code: string | null | undefined,
                                  locale: string = i18n.global.locale.value): string {
  if (v === null || v === undefined) return '—';
  const s = decimalString(v);
  if (s == null) return String(v);
  const { min, max } = displayDigits(code);
  return formatDecimal(ceilDecimal(s, max), min, max, locale);
}

/** An amount with its currency code after it: «5 250,00 USDT». */
export function formatMoney(v: string | number | null | undefined, code: string | null | undefined,
                            locale: string = i18n.global.locale.value): string {
  const n = formatDisplayAmount(v, code, locale);
  return code && n !== '—' ? `${n} ${code}` : n;
}

/**
 * A rate, with the precision of `quoteCode` — the currency it is denominated in: «80,33» (fiat), «0,0104» (fiat
 * below 1), «1,0834» (crypto).
 */
export function formatRate(v: string | number | null | undefined, quoteCode: string | null | undefined,
                           locale: string = i18n.global.locale.value): string {
  if (v === null || v === undefined) return '—';
  const s = decimalString(v);
  if (s == null) return String(v);
  const max = isCrypto(quoteCode) || Math.abs(Number(s)) < 1 ? 4 : 2;
  return formatDecimal(s, 2, max, locale);
}

// ---------------------------------------------------------------- the amount INPUT (owner rule 25.10.2026)
//
// While a client EDITS a sum the field holds digits and one decimal sign only — no thousands grouping at all, in any
// language. Any typed «,» or «.» is the decimal, shown as the language's decimal sign (ru «1234,5», en/zh «1234.5»);
// spaces and anything else typed or pasted are dropped. No guessing, so no 100× error whatever the keyboard offers.
// A pasted sum carrying both signs («1,234.56», «1.234,56») takes the LAST one as the decimal and drops the others.
// Displayed (non-editable) sums stay locale-formatted (`formatDisplayAmount`). The value sent to the server is always
// a plain decimal string.

/** The group and decimal separators of the input in `locale`; a no-break group space is typed as a plain one. */
function inputSeparators(locale: string): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const decimal = parts.find(p => p.type === 'decimal')?.value ?? '.';
  const group = (parts.find(p => p.type === 'group')?.value ?? ',').replace(/[  ]/g, ' ');
  return { group, decimal };
}

/**
 * Every «,» and «.» of an edited sum turned into the one decimal «.». A pasted sum with both signs whose earlier
 * signs each stand before exactly three digits («1,234.56», «1.234,56») takes the last sign as the decimal;
 * otherwise the first sign is the decimal and later ones are dropped — a second press of «,»/«.» after the
 * decimal («100,5» + «.») changes nothing.
 */
function unifyDecimal(s: string): string {
  const signs = [...s.matchAll(/[.,]/g)].map(m => m.index ?? 0);
  if (!signs.length) return s;
  const last = signs[signs.length - 1];
  // A paste: both signs, every earlier one before exactly three digits, and digits after the last one. A sign just
  // typed at the end («1.234» + «,») is a second press of the decimal key, not a paste — it changes nothing.
  const pastedGrouping = s.includes(',') && s.includes('.') && /\d/.test(s.slice(last + 1))
    && signs.slice(0, -1).every((at, i) => /^\d{3}$/.test(s.slice(at + 1, signs[i + 1])));
  const at = pastedGrouping ? last : signs[0];
  if (at < 0) return s;
  return `${s.slice(0, at).replace(/[.,]/g, '')}.${s.slice(at + 1).replace(/[.,]/g, '')}`;
}

/** The integer and fraction digits of an edited sum (fraction null when no decimal sign was typed). */
function splitTyped(v: string): { int: string; frac: string | null } {
  let s = String(v ?? '').replace(/[\s  ]/g, '');
  s = unifyDecimal(s);
  s = s.replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  const int = (dot >= 0 ? s.slice(0, dot) : s).replace(/\D/g, '').slice(0, 12);
  return { int, frac: dot >= 0 ? s.slice(dot + 1).replace(/\D/g, '') : null };
}

/**
 * What an amount field shows while the client edits it: digits and the decimal in the language's sign, no grouping —
 * ru «1234,5», en/zh «1234.5». The fraction is capped at `scale` digits; a just-typed decimal sign is kept so the
 * client can go on typing. A plain decimal from a link or a computed value goes through as well.
 */
export function formatAmountInput(v: string, scale: number, locale: string = i18n.global.locale.value): string {
  const { decimal } = inputSeparators(locale);
  const { int, frac } = splitTyped(v);
  if (frac === null || scale === 0) return int;
  return `${int}${decimal}${frac.slice(0, scale)}`;
}

/** The plain decimal string of an edited sum («1234,5» / «1 234.5» → «1234.5»); '' when nothing typed. */
export function parseAmountInput(v: string): string {
  const { int, frac } = splitTyped(v);
  if (!frac) return int;
  return `${int || '0'}.${frac}`;
}

/** An amount as `formatDisplayAmount` writes it, or an em dash when it is missing or not a number. */
export function formatAmountOrDash(a: string | number | null | undefined, code?: string | null): string {
  if (a === null || a === undefined) return '—';
  const n = typeof a === 'string' ? parseFloat(a) : a;
  return Number.isFinite(n) ? formatDisplayAmount(a, code) : '—';
}

/**
 * A signed operation amount as the prototype writes it (D, 01.11): sign, space, currency symbol, space, number —
 * «− $ 312,00», «+ $ 2 500,00». A currency with no symbol keeps its code after the number: «+ 12,35 USDT».
 * Not a number → the string as it came.
 */
export function formatSignedAmount(amount: string | number, currency: string): string {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (!isFinite(n)) return String(amount);
  const sign = n > 0 ? '+ ' : n < 0 ? '− ' : '';
  const abs = typeof amount === 'string' ? amount.trim().replace(/^[-+]/, '') : Math.abs(n);
  const sym = currencySymbol(currency);
  const num = formatDisplayAmount(abs, currency);
  return sym !== currency ? `${sign}${sym} ${num}` : `${sign}${num} ${currency}`;
}
